/**
 * 글자 크기(fontSize) 직렬화 호환 레이어 (P0-④) — lumir `font-size-serialization.ts` 이식.
 *
 * 저장 JSON에서 fontSize는 styles 맵이 아닌 styled-text의 **형제 키**로 둔다.
 * (구버전 SDK는 styles에 스키마 외 키가 있으면 크래시 → 형제 키는 조용히 무시되어 안전)
 *  - lowerFontSize: 저장 시 `styles.fontSize` → 형제 키 `fontSize`
 *  - liftFontSize:  로드 시 형제 키 `fontSize` → `styles.fontSize`
 *
 * 외부로 내보내는 모든 경로는 lowerFontSize를 거쳐야 한다(불변 규칙).
 * 입력을 변형하지 않으며(immutable), 변경이 없으면 동일 배열 참조를 반환한다.
 */

/** 배열 map 후 변경이 없으면 원본 참조 반환(불필요한 재생성 방지) */
function mapPreservingRef(arr, fn) {
  let changed = false;
  const next = arr.map((item) => {
    const mapped = fn(item);
    if (mapped !== item) changed = true;
    return mapped;
  });
  return changed ? next : arr;
}

/** styled-text 한 항목의 fontSize를 styles ↔ 형제 키 간 이동 */
function mapStyledItem(item, dir) {
  if (!item || typeof item !== "object" || item.type !== "text") return item;
  if (dir === "lower") {
    const fs = item.styles && item.styles.fontSize;
    if (fs == null) return item;
    const { fontSize: _removed, ...restStyles } = item.styles;
    return { ...item, styles: restStyles, fontSize: fs };
  }
  // lift: 형제 키 → styles.fontSize (둘 다 있으면 형제 키 우선)
  const fs = item.fontSize;
  if (fs == null) return item;
  const { fontSize: _lifted, ...rest } = item;
  return { ...rest, styles: { ...(item.styles || {}), fontSize: fs } };
}

/** 인라인 항목 — link는 중첩 content를 재귀 */
function mapInlineItem(node, dir) {
  if (!node || typeof node !== "object") return node;
  if (node.type === "link" && Array.isArray(node.content)) {
    const content = mapPreservingRef(node.content, (n) => mapStyledItem(n, dir));
    return content === node.content ? node : { ...node, content };
  }
  return mapStyledItem(node, dir);
}

/** 표 셀 — `{type:"tableCell", content}` 객체 / 평문 인라인 배열 두 형태 모두 처리 */
function mapCell(cell, dir) {
  if (cell && typeof cell === "object" && cell.type === "tableCell") {
    if (!Array.isArray(cell.content)) return cell;
    const content = mapPreservingRef(cell.content, (n) => mapInlineItem(n, dir));
    return content === cell.content ? cell : { ...cell, content };
  }
  if (Array.isArray(cell)) return mapPreservingRef(cell, (n) => mapInlineItem(n, dir));
  return cell;
}

function mapBlock(block, dir) {
  if (!block || typeof block !== "object") return block;
  let next = block;
  const content = block.content;

  if (Array.isArray(content)) {
    const mapped = mapPreservingRef(content, (n) => mapInlineItem(n, dir));
    if (mapped !== content) next = { ...next, content: mapped };
  } else if (
    content &&
    typeof content === "object" &&
    content.type === "tableContent" &&
    Array.isArray(content.rows)
  ) {
    const rows = mapPreservingRef(content.rows, (row) => {
      if (!row || !Array.isArray(row.cells)) return row;
      const cells = mapPreservingRef(row.cells, (cell) => mapCell(cell, dir));
      return cells === row.cells ? row : { ...row, cells };
    });
    if (rows !== content.rows) next = { ...next, content: { ...content, rows } };
  }
  // content === undefined (image/video 등) → 통과

  if (Array.isArray(block.children) && block.children.length > 0) {
    const children = mapPreservingRef(block.children, (b) => mapBlock(b, dir));
    if (children !== block.children) next = { ...next, children };
  }
  return next;
}

/** 저장 방향: styles.fontSize → 형제 키 fontSize (onContentChange 출력에 적용) */
export function lowerFontSize(blocks) {
  if (!Array.isArray(blocks)) return blocks;
  return mapPreservingRef(blocks, (b) => mapBlock(b, "lower"));
}

/** 로드 방향: 형제 키 fontSize → styles.fontSize (initialContent에 적용) */
export function liftFontSize(blocks) {
  if (!Array.isArray(blocks)) return blocks;
  return mapPreservingRef(blocks, (b) => mapBlock(b, "lift"));
}
